import React, { FC, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SILENT_MIN, STALE_MIN } from "map/liveBoard";
import { BOARD_COLORS, LEG_COLOR } from "./palette";
import LegGlyph, { Mark, MarkKind } from "./LegGlyph";

/**
 * The board's own manual, kept from the standalone page.
 *
 * It is here because the board encodes a lot in very little ink -- shape, colour, ring, line --
 * and a dispatcher who guesses wrong about any of it reads the map backwards. Keep it in step
 * with behaviour changes; a stale legend is worse than none.
 */

const Em: FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <Box component="b" sx={{ color }}>
    {children}
  </Box>
);

const Glyphs: FC<{ kind?: MarkKind; leg?: string; color?: string; children: React.ReactNode }> = ({
  kind,
  leg,
  color,
  children,
}) => (
  <Box component="li" sx={{ display: "flex", gap: 1, mb: 1, alignItems: "flex-start", listStyle: "none" }}>
    <Box sx={{ flex: "0 0 34px", pt: 0.25 }}>
      {leg ? (
        <LegGlyph leg={leg} color={color} />
      ) : (
        <svg width={22} height={22} viewBox="-11 -11 22 22" style={{ color: color || BOARD_COLORS.hub }}>
          <Mark kind={kind} x={0} y={0} r={6} />
        </svg>
      )}
    </Box>
    <Box sx={{ fontSize: 13 }}>{children}</Box>
  </Box>
);

const Section: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ fontSize: 13, "& p": { mt: 0, mb: 1.25 }, "& ul": { pl: 0, m: 0 }, "& h3": { fontSize: 13, mt: 2, mb: 0.75 } }}>
    {children}
  </Box>
);

const THRESHOLDS: [string, React.ReactNode][] = [
  [
    "Late",
    <>
      arrived ≥ 1 min after the pickup time; started but ≥ 1 min past pickup with no arrival; or past pickup and never
      started → <Em color={BOARD_COLORS.late}>red</Em> (returning vans excepted)
    </>,
  ],
  [
    "Potential late",
    <>
      now &gt; pickup − (drive min + 10 min) and not started → <Em color={BOARD_COLORS.warn}>amber</Em>
    </>,
  ],
  [
    "Silent",
    <>
      no GPS fix and no event for {SILENT_MIN} min (started trips) → <Em color={BOARD_COLORS.warn}>amber</Em>, including
      returning vans
    </>,
  ],
  [
    `GPS > ${STALE_MIN}m`,
    <>
      last fix older than {STALE_MIN} min, and the pin falls back to the last event →{" "}
      <Em color={BOARD_COLORS.warn}>amber</Em> on to-pickup / to-dropoff vans
    </>,
  ],
  [
    "No driver / vehicle",
    <>
      pending trip with the driver or vehicle unassigned → <Em color={BOARD_COLORS.warn}>amber</Em>; filtered through the
      Pending (no driver / no vehicle) status chips
    </>,
  ],
  [
    "Backhaul",
    <>
      another hub ≤ 15 drive-minutes from the dropoff point → <Em color={BOARD_COLORS.backhaul}>magenta</Em> ring when
      highlighted
    </>,
  ],
  ["Upcoming", <>pending trips with a pickup inside the chosen 2–24 h window (default 4 h; started trips always show)</>],
];

const HowToReadDialog: FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>How to read the board</Typography>
          <IconButton size="small" sx={{ ml: "auto" }} onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Marks & colours" sx={{ fontSize: 12 }} />
          <Tab label="Warnings & flags" sx={{ fontSize: 12 }} />
          <Tab label="Controls" sx={{ fontSize: 12 }} />
          <Tab label="Data & definitions" sx={{ fontSize: 12 }} />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers>
        {tab === 0 ? (
          <Section>
            <p>
              One row per <b>open trip</b> (not closed, not cancelled). Each trip is drawn as the thing that matters
              right now: the van if it has started, otherwise the pickup point it is waiting for, plus a dashed line from
              the van to its <b>next stop</b>. Three things to read — the <b>shape</b> says where the trip is in its
              life, the <b>colour</b> says whether it needs attention, and a <b>dashed ring</b> marks the exact spot (van
              and next stop) when it does.
            </p>
            <h3>Shapes</h3>
            <ul>
              <Glyphs kind="vehicle" color={LEG_COLOR.to_pickup}>
                <b>Vehicle</b> — filled dot at the latest GPS fix, labelled with the unit number. If GPS is older than{" "}
                {STALE_MIN} min the dot sits at the last trip event instead; the tooltip's <i>Pin</i> row says which.
              </Glyphs>
              <Glyphs kind="pickup" color={LEG_COLOR.to_pickup}>
                <b>Pickup point</b> — hollow triangle. Pending trips (no van yet) are drawn <i>only</i> as their pickup
                triangle, labelled with the trip number at the same zoom as unit numbers.
              </Glyphs>
              <Glyphs kind="dropoff" color={LEG_COLOR.to_dropoff}>
                <b>Dropoff point</b> — hollow square; shown once the van is at or past the pickup, because the dropoff is
                its next stop from then on.
              </Glyphs>
              <Glyphs kind="hub">
                <b>Hub</b> — diamond with a centre dot, named as you zoom in. Hubs with trips on the board are drawn a
                shade stronger.
              </Glyphs>
              <Glyphs leg="to_pickup" color={LEG_COLOR.to_pickup}>
                <b>Next-stop line</b> — van → pickup, van → dropoff, or van → hub (returning; finer dashes). Takes the
                trip's colour.
              </Glyphs>
            </ul>
            <h3>Shape = where the trip is · colour = does it need attention</h3>
            <p>
              Every trip takes <b>one</b> colour: <Em color={BOARD_COLORS.late}>red</Em> if it is late,{" "}
              <Em color={BOARD_COLORS.warn}>amber</Em> for a warning, otherwise the calm base colour of its leg. The
              colour goes on the van, its line, its next-stop mark, the ring, the list row, the tooltip and the detail
              card, so one glance shows what needs attention — and the tooltip's first line says why. Which of the two
              moving legs a green trip is on is told by the mark at the end of its line, triangle versus square.
            </p>
            <ul>
              <Glyphs leg="pending" color={LEG_COLOR.pending}>
                <b>Pending</b> — blue; not started, drawn at the pickup point and labelled with the trip number. Turns{" "}
                <Em color={BOARD_COLORS.warn}>amber</Em> when a driver or vehicle is still missing or it is potentially
                late, <Em color={BOARD_COLORS.late}>red</Em> when the pickup time has passed and it never started.
              </Glyphs>
              <Glyphs leg="to_pickup" color={LEG_COLOR.to_pickup}>
                <b>Heading to pickup</b> — green; <i>Begin Trip</i> logged, not yet arrived. The line points at the
                pickup triangle.
              </Glyphs>
              <Glyphs leg="to_dropoff" color={LEG_COLOR.to_dropoff}>
                <b>To dropoff</b> — green; <i>Arrived at Pickup</i> or <i>Depart with Crew</i> logged, so the next stop
                is the dropoff. The line points at the dropoff square.
              </Glyphs>
              <Glyphs leg="return" color={LEG_COLOR.return}>
                <b>Returning to hub</b> — slate; <i>Drop off Crew</i> logged, <i>End Trip</i> not yet. An empty van is
                idle, so it only changes colour (amber) when it goes <i>silent</i>.
              </Glyphs>
            </ul>
            <p>
              Hub names appear as you zoom in; unit numbers and trip numbers one step further. Hovering or selecting
              anything labels it regardless of zoom. Backhaul uses magenta, which never means status; gold is reserved
              for hover and selection.
            </p>
          </Section>
        ) : null}

        {tab === 1 ? (
          <Section>
            <p>
              Red = late, amber = warning. A trip that needs attention also gets a dashed ring in the same colour around
              the van <i>and</i> the point it is heading to (pending trips: around the pickup triangle), so it stands out
              at any zoom. Hover it and the tooltip's first line spells out the reasons; the list row and the detail card
              repeat them. In <i>Vehicles only</i> mode the ring on the next stop is hidden with the mark.
            </p>
            <ul>
              <Glyphs kind="vehicle" color={BOARD_COLORS.late}>
                <b>Late</b> — red. The van arrived at the pickup ≥ 1 min after the pickup time; <i>or</i> the trip has
                started, the pickup time passed ≥ 1 min ago and the van still has not arrived (the count keeps growing);{" "}
                <i>or</i> the pickup time has passed and the trip never started. Returning vans keep their slate: the
                crew has already been delivered.
              </Glyphs>
              <Glyphs kind="vehicle" color={BOARD_COLORS.warn}>
                <b>Warning</b> — amber. <b>Potential late</b>: not started and already past pickup − drive time − 10 min.{" "}
                <b>No driver / no vehicle</b> on a pending trip. <b>GPS older than {STALE_MIN} min</b> on a moving van —
                the dot then sits at the last trip event. <b>Silent</b>: no GPS fix <i>and</i> no trip event for{" "}
                {SILENT_MIN}+ min, the only thing that colours a returning van.
              </Glyphs>
              <Glyphs kind="vehicle" color={LEG_COLOR.to_pickup}>
                <b>All good</b> — the leg's base colour, no ring. Green and red sit at similar brightness for
                colour-blind readers, so the ring is the tie-breaker: a ringed van always needs attention, an unringed
                one never does.
              </Glyphs>
              <Glyphs kind="vehicle" color={BOARD_COLORS.backhaul}>
                <b>Backhaul opportunity</b> — solid magenta ring, the only non-status colour on the map. Another active
                hub is within 15 drive-minutes of this trip's dropoff, so an empty van could bring a crew back for it.
                Shown when you press <i>Highlight backhaul opportunities</i>, and automatically for trips that could
                backhaul <i>into</i> a hub you have selected in the Hubs filter.
              </Glyphs>
            </ul>
            <h3>KPI chips (header)</h3>
            <p>
              <b>Trip status</b> — to pickup · to dropoff · returning · pending (assigned) · pending (no driver) ·
              pending (no vehicle), each with its map glyph. A pending trip missing both a driver and a vehicle counts
              under both. <b>Warnings</b> — late · potential late · no GPS/event {SILENT_MIN}m. <b>Other</b> — GPS &gt;{" "}
              {STALE_MIN}m · backhaul opps. Every chip is a one-click filter and they combine: several status chips show
              trips on <i>any</i> of those legs, several warning chips show trips with <i>any</i> of those flags, and the
              two groups intersect. Counts follow the <i>Upcoming</i> window and the hub, client and search filters — but
              not the status chips themselves, which are what the chips toggle.
            </p>
          </Section>
        ) : null}

        {tab === 2 ? (
          <Section>
            <ul style={{ paddingLeft: 18, listStyle: "disc" }}>
              <li>
                <b>Hover</b> a mark for the quick tooltip (its first line says why the trip is red or amber);{" "}
                <b>click</b> a mark or a list row to open the <b>trip detail card</b> in the side panel with the full
                timeline, driver, vehicle, pickup/dropoff, GPS age, backhaul hubs, train number and an{" "}
                <b>Open in Flow</b> link. Click again, × or <kbd>Esc</kbd> to close. Other trips dim while one is
                selected.
              </li>
              <li>
                <b>Upcoming</b> — how far ahead (2 / 4 / 6 / 8 / 12 / 24 h, default 4 h) to show trips that have{" "}
                <i>not started yet</i>. Started trips always show.
              </li>
              <li>
                <b>Trip status chips</b> — toggle statuses on and off; pending is split into assigned / no driver / no
                vehicle. <b>Layers</b> — Hubs, Unit / trip labels, <b>Vehicles only</b> (hides pickup and dropoff marks,
                their rings and the lines; pending trips have no van, so they drop out). <b>Other</b> — Highlight
                backhaul opportunities.
              </li>
              <li>
                <b>Search</b> — trip #, driver, unit, train, hub, client, pickup or dropoff name.
              </li>
              <li>
                <b>Hubs</b> — multiselect. Shows the selected hubs' own trips <i>plus</i> trips whose dropoff is within
                15 min of one of them (possible backhauls, magenta-ringed). Each hub shows both counts, e.g. “Amarillo ·
                18 + 6 backhaul”.
              </li>
              <li>
                <b>Clients</b> — multiselect with counts. <i>RailCrew Xpress</i> (internal) is unchecked by default and
                the button reads “All but RailCrew Xpress”; tick it or use Select all to include it.
              </li>
              <li>
                <b>Counts in the dropdowns</b> follow the other active filters: pick a client and the hub list shows how
                many of <i>that client's</i> trips sit at each hub, and vice versa.
              </li>
              <li>
                <b>Sort</b> — pickup time · latest event · stalest GPS · hub. <b>Show everything</b> resets every filter,
                layer and selection and puts Upcoming back to 4 h.
              </li>
              <li>
                <b>Map</b> — drag to pan, wheel or ± to zoom, <b>FIT</b> zooms to the trips on the board, <b>US</b> shows
                the whole country. The map never zooms out past the US; coordinates outside the continental US (0, 0 is
                the usual case) are treated as invalid — not drawn and ignored by the zoom, but listed on the detail
                card.
              </li>
              <li>
                <b>Refresh</b> — re-queries now; otherwise the board refreshes itself every 60 s while the tab is
                visible. The header shows the snapshot time, and every “x min ago” is relative to it.
              </li>
            </ul>
          </Section>
        ) : null}

        {tab === 3 ? (
          <Section>
            <h3>Where the numbers come from</h3>
            <ul style={{ paddingLeft: 18, listStyle: "disc" }}>
              <li>
                One read-only query behind OrderApi: open trips are <code>IsTripClosed = 0</code> and{" "}
                <code>Cancelled = 0</code>, excluding the placeholder drivers <i>Mother trip</i>, <i>delete me</i> and{" "}
                <i>Please Read Notes</i>. Every flag is computed in SQL, so this board and any other consumer agree.
              </li>
              <li>
                <b>Leg</b> comes from the trip's events: <i>Begin Trip</i> = heading to pickup → <i>Arrived at Pickup</i>{" "}
                or <i>Depart with Crew</i> = to dropoff → <i>Drop off Crew</i> = returning to hub → <i>End Trip</i> =
                ended, awaiting close.
              </li>
              <li>
                <b>Vehicle position</b>: the latest telematics fix per vehicle; if older than {STALE_MIN} min, the last
                event's coordinates; pending trips sit on their pickup point. A few devices report future-dated fixes —
                the position is still the latest known, and the age reads “clock ahead”.
              </li>
              <li>
                <b>Pickup / dropoff</b>: the trip's points of service, with ad-hoc coordinates overriding. <b>Hubs</b>:
                active, not suspended, with coordinates. Any coordinate outside the continental US is treated as
                missing: not drawn, ignored by the zoom, and listed on the detail card under <i>Invalid coords</i>.
              </li>
              <li>
                <b>Times</b> are shown in the trip's own time zone for the pickup, and as relative ages elsewhere.
              </li>
              <li>
                <b>Backhaul</b>: other active, non-staging hubs whose drive time to the dropoff's client point is ≤ 15
                min.
              </li>
              <li>
                <b>Drive time to pickup</b> (for potential-late): the trip's own drive time when recorded, else the
                hub→pickup drive time, else just the 10-minute buffer.
              </li>
            </ul>
            <h3>Thresholds</h3>
            <Table size="small">
              <TableBody>
                {THRESHOLDS.map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {k}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default HowToReadDialog;
